import React, { useState, useRef, useEffect } from 'react';
import {
  ZoomIn,
  ZoomOut,
  Maximize2,
  Lock,
  Unlock,
  RotateCcw,
  ChevronDown,
  Bug,
  BarChart3,
  Gamepad2,
  Globe,
  Monitor,
} from 'lucide-react';
import { cn } from '../../utils/cn';
import { useLivePreviewStore } from '../../store/livePreviewStore';
import { DeviceSelector } from './DeviceSelector';
import { DebugOptions } from './DebugOptions';
import { StatsPanel } from './StatsPanel';
import { InputSimulator } from './InputSimulator';
import { SCALE_PRESETS } from '../../utils/device-presets';

type DropdownType = 'scale' | 'debug' | 'stats' | 'input' | null;

export const ViewportToolbar: React.FC = () => {
  const [activeDropdown, setActiveDropdown] = useState<DropdownType>(null);
  const [isCompact, setIsCompact] = useState(false);
  const toolbarRef = useRef<HTMLDivElement>(null);

  const {
    viewport,
    previewMode,
    setPreviewMode,
    setScale,
    setFitToPanel,
    toggleAspectRatioLock,
  } = useLivePreviewStore();

  // Check width and set compact mode
  useEffect(() => {
    const checkWidth = () => {
      if (toolbarRef.current) {
        setIsCompact(toolbarRef.current.offsetWidth < 600);
      }
    };

    checkWidth();
    const resizeObserver = new ResizeObserver(checkWidth);
    if (toolbarRef.current) {
      resizeObserver.observe(toolbarRef.current);
    }

    return () => resizeObserver.disconnect();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (toolbarRef.current && !toolbarRef.current.contains(e.target as Node)) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleDropdown = (dropdown: DropdownType) => {
    setActiveDropdown(activeDropdown === dropdown ? null : dropdown);
  };

  const currentScalePercent = Math.round(viewport.scale * 100);

  return (
    <div ref={toolbarRef} className="relative flex items-center gap-1 px-2 py-1 bg-zinc-800 border-b border-zinc-700">
      {/* Device Selector */}
      <DeviceSelector compact={isCompact} />

      {/* Scale controls - hide in compact mode */}
      {!isCompact && (
        <>
          <div className="w-px h-5 bg-zinc-700 mx-1" />

          <div className="flex items-center gap-0.5">
            <button
              onClick={() => setScale(viewport.scale - 0.25)}
              disabled={viewport.scale <= 0.25}
              className="p-1.5 hover:bg-zinc-700 rounded disabled:opacity-50 disabled:cursor-not-allowed"
              title="Zoom Out"
            >
              <ZoomOut size={14} />
            </button>

            <div className="relative">
              <button
                onClick={() => toggleDropdown('scale')}
                className="flex items-center gap-0.5 px-1.5 py-1 hover:bg-zinc-700 rounded text-xs min-w-[50px]"
              >
                <span>{currentScalePercent}%</span>
                <ChevronDown size={10} />
              </button>

              {activeDropdown === 'scale' && (
                <div className="absolute top-full left-0 mt-1 bg-zinc-800 border border-zinc-700 rounded shadow-lg z-50">
                  {SCALE_PRESETS.map((preset) => (
                    <button
                      key={preset.value}
                      onClick={() => {
                        setScale(preset.value);
                        setActiveDropdown(null);
                      }}
                      className={cn(
                        'w-full px-3 py-1.5 text-left text-sm hover:bg-zinc-700',
                        viewport.scale === preset.value && 'text-blue-400'
                      )}
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button
              onClick={() => setScale(viewport.scale + 0.25)}
              disabled={viewport.scale >= 2}
              className="p-1.5 hover:bg-zinc-700 rounded disabled:opacity-50 disabled:cursor-not-allowed"
              title="Zoom In"
            >
              <ZoomIn size={14} />
            </button>
          </div>
        </>
      )}

      {/* Fit, Lock, Reset - show individually on wide, grouped on compact */}
      {!isCompact && (
        <>
          <div className="w-px h-5 bg-zinc-700 mx-1" />

          <button
            onClick={() => setFitToPanel(!viewport.fitToPanel)}
            className={cn(
              'p-1.5 rounded',
              viewport.fitToPanel ? 'bg-blue-600 text-white' : 'hover:bg-zinc-700'
            )}
            title="Fit to Panel"
          >
            <Maximize2 size={14} />
          </button>

          <button
            onClick={toggleAspectRatioLock}
            className={cn(
              'p-1.5 rounded',
              viewport.aspectRatioLocked ? 'text-blue-400' : 'hover:bg-zinc-700'
            )}
            title={viewport.aspectRatioLocked ? 'Unlock Aspect Ratio' : 'Lock Aspect Ratio'}
          >
            {viewport.aspectRatioLocked ? <Lock size={14} /> : <Unlock size={14} />}
          </button>

          <button
            onClick={() => setScale(1)}
            className="p-1.5 hover:bg-zinc-700 rounded"
            title="Reset Zoom"
          >
            <RotateCcw size={14} />
          </button>
        </>
      )}

      {/* Preview mode toggle */}
      <div className="w-px h-5 bg-zinc-700 mx-1" />
      <button
        onClick={() => setPreviewMode(previewMode === 'iframe' ? 'canvas' : 'iframe')}
        className={cn(
          'p-1.5 rounded',
          previewMode === 'iframe' ? 'bg-blue-600 text-white' : 'hover:bg-zinc-700'
        )}
        title={previewMode === 'iframe' ? 'Switch to Canvas Preview' : 'Switch to Iframe Preview'}
      >
        {previewMode === 'iframe' ? <Globe size={14} /> : <Monitor size={14} />}
      </button>

      {/* Spacer */}
      <div className="flex-1 min-w-2" />

      {/* Resolution display - hide on very compact */}
      {!isCompact && (
        <div className="text-xs text-zinc-400 mr-2">
          {viewport.customWidth}×{viewport.customHeight}
        </div>
      )}

      {/* Debug, Stats, Input dropdowns - hide in compact mode */}
      {!isCompact && (
        // Wide mode: individual dropdown buttons
        <>
          <div className="relative">
            <button
              onClick={() => toggleDropdown('debug')}
              className={cn(
                'flex items-center gap-1 px-2 py-1 rounded text-xs',
                activeDropdown === 'debug' ? 'bg-zinc-700' : 'hover:bg-zinc-700'
              )}
              title="Debug Options"
            >
              <Bug size={14} />
              <ChevronDown size={10} />
            </button>

            {activeDropdown === 'debug' && (
              <div className="absolute top-full right-0 mt-1 bg-zinc-800 border border-zinc-700 rounded shadow-lg z-50 w-64">
                <DebugOptions />
              </div>
            )}
          </div>

          <div className="relative">
            <button
              onClick={() => toggleDropdown('stats')}
              className={cn(
                'flex items-center gap-1 px-2 py-1 rounded text-xs',
                activeDropdown === 'stats' ? 'bg-zinc-700' : 'hover:bg-zinc-700'
              )}
              title="Performance Stats"
            >
              <BarChart3 size={14} />
              <ChevronDown size={10} />
            </button>

            {activeDropdown === 'stats' && (
              <div className="absolute top-full right-0 mt-1 bg-zinc-800 border border-zinc-700 rounded shadow-lg z-50 w-64">
                <StatsPanel />
              </div>
            )}
          </div>

          <div className="relative">
            <button
              onClick={() => toggleDropdown('input')}
              className={cn(
                'flex items-center gap-1 px-2 py-1 rounded text-xs',
                activeDropdown === 'input' ? 'bg-zinc-700' : 'hover:bg-zinc-700'
              )}
              title="Input Simulation"
            >
              <Gamepad2 size={14} />
              <ChevronDown size={10} />
            </button>

            {activeDropdown === 'input' && (
              <div className="absolute top-full right-0 mt-1 bg-zinc-800 border border-zinc-700 rounded shadow-lg z-50 w-72">
                <InputSimulator />
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};
